import type { ReactNode } from 'react';

import { Logo } from './Logo';

export interface KioskCardProps {
  /**
   * The inner content column. The frames use 448px on the form screens and 384px on Welcome — a narrower
   * measure for a screen that is read rather than filled in.
   *
   * **528 is a DESKTOP-ONLY widening, not a flat width.** `02 · Check-in — Desktop` [12055:10267] wraps its
   * content in a full-card-width band with 120px of side padding — 768 − 240 = 528 — while
   * `02 · Check-in — iPad` [12055:10291] keeps the same 448 as every other form screen. Read as a flat
   * number it would put a 528 column inside a 553 card at tablet, which is why it selects
   * `448 → 528 at 1440` rather than one measure at every width. It is the only frame in the file that does
   * this; every other desktop frame is 448 (or Welcome's 384).
   */
  readonly contentWidth?: 448 | 384 | 488 | 528;
  /** The header strip under the logo — Back on the left, a status badge on the right. Optional: not every screen has one. */
  readonly header?: ReactNode;
  /**
   * A full-width band between the header and the content column, for the journey rail.
   *
   * Its own slot because the rail is 500px wide inside a 553px card while the content column is 448 —
   * putting it in the column capped it 52px narrower than drawn, which is the same class of error that made
   * the footer button 57px too narrow before the footer got its own slot.
   */
  readonly band?: ReactNode;
  readonly children: ReactNode;
  /** The footer strip below a divider, where the primary action lives. */
  readonly footer?: ReactNode;
  /**
   * The vertical rhythm between the card's sections — and it is a THIRD case, not a preference.
   *
   * `spaced` (the default) is the 24px rhythm below, read off check-in: logo ends 111 → header 135 → ends
   * 183 → content 207. Every form screen draws it.
   *
   * `flush` stacks them with no gap at all, and its own 24px at mobile goes too. That is what the nine
   * `Blocked — cannot continue` frames draw — measured on `Blocked · consent — Desktop` (12424:24993):
   * card at 92, Logo 92→203, Header 203→251, Content 251→482, Footer at 482, each boundary exact. Their
   * `Content` carries 32px of its own top and bottom padding instead, so the breathing room is inside the
   * column rather than between the slots.
   *
   * Left to the default, every one of those nine viewports rendered its header 24px low and everything
   * below it 24 lower again — reported by `design-qa/diff.mjs` as `Header dy+24` … `Button dy+24` on all
   * nine, which is how the difference was found rather than assumed.
   *
   * `banded` is `flush` from `sm` up and `spaced` below it, which is not a compromise between the two but
   * what `10 · Submitted` is drawn as at all three widths — the only card in the file whose slot above the
   * content is the journey rail and nothing else. Read live 2026-09-11 from the frames resolved by NAME:
   *
   *   — Desktop (12021:42080) card 92, Logo 92→203, Stepper 203→247, Content 247, each boundary flush
   *   — iPad    (12021:41993) identical, and `Content` carries `pad[T32 B32]` like the `flush` screens
   *   — Mobile  (12021:41896) bar ends 52 → Logo 76 → ends 147 → Rail 171 → ends 219 → Content 243,
   *     three 24px gaps, and `Content` carries `pad[T0 B32]` — the air is BETWEEN the slots there
   *     because at 375 there is no card enclosing them.
   *
   * Neither existing value draws that: `spaced` keeps its 24px gap at every width (stepper 24px low at
   * 1440 and 820), `flush` drops it at every width (the 375 frame's three gaps gone).
   */
  readonly rhythm?: 'spaced' | 'flush' | 'banded';
  /**
   * Whether a card with NOTHING above its content takes the 48px desktop inset.
   *
   * `true` (the default) is Welcome and Have-you-applied: both put their `Logo` at y=140 inside a card at
   * y=92, and both were inheriting it before this prop existed.
   *
   * `false` is the device-enrolment section, where the card opens flush — `01 · PIN · Enter — Desktop`
   * (12021:41617) draws the card at y=24 and its `Logo` at y=24, at 1440 as at 820. That is the same shape
   * as `10 · Submitted`, which reaches it the other way: it is headerless but not bare, because it passes a
   * band. These six states pass neither, so without a way to say so they would render 48px low at desktop
   * and every box below them with it.
   */
  readonly bareInset?: boolean;
}

/**
 * The white card every kiosk screen is built on [Figma · kiosk, all flows].
 *
 * **The card is responsive; the content column is not.** That is the shape of the design and it is easy to
 * miss, because only one number changes:
 *
 *   iPad    (820)   card 553   content 448   gutter  52.5   footer button 505
 *   Desktop (1440)  card 768   content 448   gutter 160     footer button 720
 *
 * An earlier build capped the card at 553 everywhere, so a desktop browser rendered the tablet card — the
 * form looked right in isolation but sat in far too little white space, which is what review kept catching
 * as "the margins are different". The content column stays 448 at every width; only the card grows.
 *
 * Below 553 the card simply fills the viewport, which is the 375 frame.
 */
/**
 * The three rhythms a card WITH A SLOT ABOVE ITS CONTENT can have — see the `rhythm` prop for where each
 * was measured.
 *
 * Both halves of `spaced` travel together on purpose: the mobile `mt-6` is the same 24px rule as the
 * `gap-6`, expressed at the one width where the card has no top edge of its own, so a screen that stacks
 * its slots flush stacks flush against the language bar too. `banded` keeps that mobile half and drops the
 * gap from `sm` up, which is the one shape the other two cannot express.
 */
const SPACING = {
  spaced: 'mt-6 gap-6 sm:mt-0',
  flush: 'gap-0',
  banded: 'mt-6 gap-6 sm:mt-0 sm:gap-0',
} as const;

export function KioskCard({
  header,
  band,
  children,
  footer,
  contentWidth = 448,
  rhythm = 'spaced',
  bareInset = true,
}: KioskCardProps) {
  const COLUMN_CLASS = {
    384: 'max-w-[384px]',
    448: 'max-w-[448px]',
    // Responsive on purpose — see the prop doc: 448 at tablet, 528 only from 1440 up.
    528: 'max-w-[448px] min-[1440px]:max-w-[528px]',
    // `02 · Have you applied?` widens only at desktop: the frame draws its column 488 there (768 less the
    // band's 140 a side) and 384 at tablet, which is the same shape as 528 above with a different number.
    488: 'max-w-[384px] min-[1440px]:max-w-[488px]',
  } as const;
  const columnClass = COLUMN_CLASS[contentWidth];

  /*
   * The 24px rhythm belongs to the CARD WITH A HEADER, not to every card. Measured on both frames:
   *
   *   check-in  logo ends 111 → header 135 → ends 183 → content 207 → ends 775 → footer 799   (24 apart)
   *   welcome   logo ends 111 → content 111 …………………… ends 265 → footer 265               (flush)
   *
   * Applying the gap everywhere pushed all of Welcome down 24px and made its card 62px too tall. The
   * header is what introduces the rhythm, so it is what selects it.
   *
   * The headerless card also gains 48px ABOVE the logo block at desktop only — Welcome's frame puts its
   * Logo at y=48 inside the card where tablet puts it at 0, and check-in keeps 0 at both. It is padding on
   * the CARD rather than on the block, since the frame's logo block stays 111 tall at every width.
   */
  const hasHeader = header !== undefined;
  /*
   * The rhythm and the 48px desktop inset are selected by whether ANY slot sits above the content, not by
   * the header alone — because the two things that branch on it are true of the band as well.
   *
   * `min-[1440px]:py-12` is the headerless card's, and it is read off the two frames that really have
   * nothing above their content: `01 · Welcome — Desktop` and `02 · Have you applied? — Desktop` both put
   * their `Logo` at y=140 inside a card at y=92. `10 · Submitted — Desktop` puts its `Logo` at y=92, flush
   * with the card — it is headerless but NOT bare, and it was inheriting 48px the frame does not draw,
   * which the gate reported as `Logo dy+48` · `Stepper dy+48` · `Content dy+48`.
   *
   * Those two frames are also the reason this is a widened condition rather than a changed one: Welcome
   * and Have you applied have no band either, so their classes are untouched, and `10 · Submitted` is the
   * only card in the app that passes a band without a header.
   */
  const hasSlotAboveContent = hasHeader || band !== undefined;
  /*
   * `gap-6` is measured, not chosen: in the frame the card's sections sit 24px apart — the logo block ends
   * at 111.12 and the header starts at 135.12; the header ends at 183.12 and the content starts at 207.12;
   * the footer opens 24px after the content. The first build stacked them flush, which is why the rule
   * under the header sat tight against the heading below it.
   */
  const headerSlot = hasHeader ? (
    /*
      CENTRED, not top-aligned. The frame's header puts the Back control at y=0 in a 36-tall row and the
      status chip at y=8 — exactly (36 − 20) / 2 — so the two read as one line. `items-start` pinned the
      chip to the top of the row, which is why it sat visibly higher than the word Back on every screen
      that has both. Reported by the Operator, 2026-09-02.
    */
    /*
      Two rules, both from the `Header` component the frames instance on every card
      [I12446:28079 and I12302:30402, read live 2026-09-08]. It is a 48-tall row: a `Left` slot at y=0
      that is 36 tall, a `Right` slot holding the 60 x 20 chip at y=8 — exactly (36 − 20) / 2, which is
      why the two read as one line — and 12px of clearance under both.

      **`min-h-9`, because the row is 36 tall whether or not Back is in it.** On the closeout the frame
      keeps the same instance with its `Back button` set `hidden`, so the row still measures 36. Here the
      height came from the tallest child, so a header carrying only the chip collapsed to 20 + 12 = 32 and
      lifted everything below it by 15px on the closeout and the five other cards with that shape.

      **`ml-auto` on a lone CHIP, because `justify-between` cannot move one item.** With two children it
      pushes the chip to the card's right edge, which is where the frame's `Right` slot sits (x=684 of a
      768 card, and x=1020 of 1104 at desktop). With one it leaves it at the start — so the closeout's chip
      sat mid-card. The frame right-aligns that slot in both cases, hidden Back or not.

      ⚠️ Keyed on `[data-badge]`, NOT on `:only-child` alone. Six cards pass the chip by itself and one —
      `02 · Check-in`, the screen every candidate passes through — passes **Back** by itself, whose slot is
      the LEFT one (the live frame gives that screen a 69 x 36 `Left` and an empty `Right`). A rule that
      only counted children threw its Back to the far right edge, into the chip's slot.
    */
    <div className="w-full border-b border-solid border-[var(--divider-border)] px-6 pb-3">
      <div className="flex min-h-9 w-full items-center justify-between gap-2 [&>[data-badge]:only-child]:ml-auto">
        {header}
      </div>
    </div>
  ) : null;

  const bandSlot =
    band === undefined ? null : (
      <div className="flex w-full max-w-[500px] items-start px-6 sm:px-0">{band}</div>
    );

  return (
    <div
      /*
       * Below the tablet width the frame draws NO CARD: the sections run full-bleed at 375 with the content
       * inset 24, no rounded corners and no elevation. Keeping a floating card there cost 32px of content
       * width and put a shadow on a screen that has none.
       */
      /*
       * `mt-6 sm:mt-0` on a card WITH a header: at 375 the page sets no gap, because the distance from the
       * language bar to the card depends on the header — every mobile frame that has one opens its logo
       * block 24px below the bar, and Welcome, which has none, opens flush. Same reason the 24px section
       * rhythm below is selected by the header rather than applied to every card.
       */
      /*
        The arbitrary shadow value carries the `shadow:` TYPE HINT, and the class below is the only place
        in this file that may spell one out.

        ⚠️ Do not write the bracketed form in prose here. Tailwind's extractor scans the raw file, comments
        included, so a class-shaped string in a comment becomes a real CSS rule — an earlier draft of this
        note used an ellipsis inside the brackets and generated `box-shadow: var(...)` with a literal
        ellipsis, which fails the build in lightningcss with `Unexpected token Ident`.

        Tailwind cannot tell whether an arbitrary shadow value is a box-shadow or a shadow colour,
        and for a bare `var()` it guesses COLOUR: the generated rule was
        `--tw-shadow-color: var(--elevation-1); --tw-shadow: var(--tw-shadow-colored)`, and nothing ever
        sets `--tw-shadow-colored`, so it resolved to `0 0 #0000`. Every card in the app rendered flat on
        the grey page — in the dev CSS and in `dist/`, so it was shipping — while the token itself was
        correct and `index.css` documented three stacked shadows nobody could see. `shadow:` forces the
        box-shadow reading.
      */
      className={`flex w-full flex-col items-center bg-[var(--card)] sm:max-w-[553px] sm:overflow-hidden sm:rounded-[var(--radius-lg)] sm:shadow-[shadow:var(--elevation-1)] min-[1440px]:max-w-[768px] ${hasSlotAboveContent ? SPACING[rhythm] : bareInset ? 'min-[1440px]:py-12' : ''}`}
    >
      {/*
        The logo block is 71 tall at 375 and 111 from `sm` up — 12px of padding either side of the
        wordmark on mobile, 40 above and 24 below on the wider frames [`02 · Check-in — Mobile`
        12055:10318: block y=76 h=71.12, logo at y=12; `— Desktop` 12055:10271: h=111.12, logo at y=40].

        The comment above used to assert the block "stays 111 tall at every width". It does not, and the
        cost was not local: the extra 40px pushed the header, the content and the footer of EVERY mobile
        screen down by exactly that, which the gate reported as `Header dy+40` on eight viewports and as a
        matching `Actions dy+40` on five. One number, forty pixels, every phone-width screen in the app.
      */}
      <div className="flex w-full items-start justify-center px-6 pb-3 pt-3 sm:pb-6 sm:pt-10">
        <Logo />
      </div>

      {/*
       * The rail sits ABOVE the header, which is the order every live frame that has one uses — Jobs, Job
       * detail, Finish and the pay card all draw Logo → Stepper → Header → Content.
       *
       * It was built the other way round, from frames that have since been deleted (they drew
       * Logo → Header → Stepper). The deleted frames stayed measurable because the fidelity config still
       * pointed at their node ids, so the check went on passing against a design nobody could open.
       */}
      {bandSlot}
      {headerSlot}

      <div className={`flex w-full flex-col items-start px-6 sm:px-0 ${columnClass}`}>
        {children}
      </div>

      {/*
        Full card width with a 24px inset, NOT the content column: the frame draws a 553-wide footer holding
        a 505-wide button. Constraining it to the 448 content column made the primary action 57px narrower
        than designed — a difference no per-control measurement would surface, because the button itself was
        internally consistent.
      */}
      {/*
        16px above the action and 16px below it, and a 16px side inset at mobile that opens to 24 from `sm`.

        Measured on the live frames rather than assumed: every `Actions` box is 84 tall around a 52 button —
        Welcome (375 and 1440), Check-in, Finish, Job detail. The header-less branch used to open `pt-10`,
        a rule read off `12021:40340`; that node has since been deleted, and the frame that replaced it uses
        the same 16 as everything else. It is one rhythm now, not two.

        The header-less footer is NOT capped to the content column. Measured on the live frames: it runs the
        full card width at 820 and 375, and 488 at 1440 (that band carries 140px of side padding, so
        768 − 280). Capping it to the column made Welcome's action band 384 against a drawn 488, and at 820
        it squeezed the secondary door onto a second line the design does not have.

        The side inset is 16 at mobile, not 24: every frame that draws an `Actions` band insets its button
        16 there — check-in, Finish and Job detail all draw 343 at x=16 in a 375 frame. Held at 24 it made
        the primary action 16px narrow on three screens at once, which is the kind of difference that reads
        as fine on each screen alone.
      */}
      {footer !== undefined && (
        <div
          className={`flex w-full flex-col items-start gap-3 px-4 pb-4 pt-4 sm:px-6 ${hasHeader ? 'border-t border-solid border-[var(--divider-border)]' : 'min-[1440px]:max-w-[488px]'}`}
        >
          {footer}
        </div>
      )}
    </div>
  );
}
